import React from 'react';
import { Calculator, Activity, Target, Scale, ChevronDown, ChevronUp, Calendar, Clock, Apple } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета потребности в калориях
 */
const ResultsOverview = ({ results, formData }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  if (!results) return null;

  // Получение цветового кода для секций в зависимости от цели
  const getColorByGoal = () => {
    if (formData.goal.includes('loss')) return 'emerald';
    if (formData.goal.includes('gain')) return 'blue';
    return 'indigo';
  };
  
  const color = getColorByGoal();

  return (
    <ToolResult title="Результаты расчета потребности в калориях">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Целевые калории */}
        <div className={`rounded-lg bg-gradient-to-br from-${color}-50 to-${color}-100 dark:from-${color}-900/30 dark:to-${color}-900/20 p-5 border border-${color}-200 dark:border-${color}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${color}-700 dark:text-${color}-300 mb-1.5 flex items-center gap-1.5`}>
            <Calculator className="w-4 h-4"/>
            Рекомендуемое потребление калорий
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${color}-800 dark:text-${color}-200`} aria-live="polite">
              {formatNumber(results.dailyCalories, 0)} ккал
            </span>
            <span className={`text-sm font-medium text-${color}-600 dark:text-${color}-400`}>
              в день
            </span>
          </div>
          <p className={`text-xs text-${color}-600 dark:text-${color}-400 mt-1`}>
            {formData.goal.includes('loss') 
              ? `Дефицит ${Math.abs(results.calorieDeficitSurplus)} ккал от поддерживающего режима` 
              : formData.goal.includes('gain')
                ? `Профицит ${results.calorieDeficitSurplus} ккал от поддерживающего режима`
                : 'Калории для поддержания текущего веса'}
          </p>
        </div>

        {/* Макронутриенты */}
        <div className={`rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm`}>
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Apple className="w-4 h-4"/>
            Рекомендуемые макронутриенты
          </h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <span className="text-lg font-bold text-blue-700 dark:text-blue-300 block">
                {results.protein} г
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400">
                Белки ({results.macroRatio.protein}%)
              </span>
            </div>
            <div>
              <span className="text-lg font-bold text-amber-600 dark:text-amber-300 block">
                {results.fat} г
              </span>
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Жиры ({results.macroRatio.fat}%)
              </span>
            </div>
            <div>
              <span className="text-lg font-bold text-green-600 dark:text-green-300 block">
                {results.carbs} г
              </span>
              <span className="text-xs text-green-600 dark:text-green-400">
                Углеводы ({results.macroRatio.carbs}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Визуализация макронутриентов */}
      <div className="mb-5">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Соотношение макронутриентов:
        </h4>
        
        <div className="w-full h-7 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
          <div 
            className="h-full bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
            style={{ width: `${results.macroRatio.protein}%` }}
          >
            {results.macroRatio.protein}%
          </div>
          <div 
            className="h-full bg-amber-500 flex items-center justify-center text-xs text-white font-medium" 
            style={{ width: `${results.macroRatio.fat}%` }}
          >
            {results.macroRatio.fat}%
          </div>
          <div 
            className="h-full bg-green-500 flex items-center justify-center text-xs text-white font-medium" 
            style={{ width: `${results.macroRatio.carbs}%` }}
          >
            {results.macroRatio.carbs}%
          </div>
        </div>
        
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>Белки ({results.protein} г)</span>
          <span>Жиры ({results.fat} г)</span>
          <span>Углеводы ({results.carbs} г)</span>
        </div>
      </div>

      {/* Результаты по снижению веса */}
      {formData.goal.includes('loss') && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800 mb-5">
          <h4 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-3 flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Прогноз снижения веса
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {Math.abs(results.weeklyWeightChange)} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">в неделю</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {Math.abs(results.weeklyWeightChange * 4).toFixed(1)} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">в месяц</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {results.goalWeight} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">целевой вес</div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-700 dark:text-emerald-300">
              Примерное время до достижения цели:
            </span>
            <span className="flex items-center font-medium">
              <Clock className="w-4 h-4 mr-1 text-emerald-500" />
              <span className="text-emerald-700 dark:text-emerald-300">
                {results.timeToGoal} {formatWeeks(results.timeToGoal)}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Результаты по набору веса */}
      {formData.goal.includes('gain') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800 mb-5">
          <h4 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-3 flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Прогноз набора массы
          </h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {results.weeklyWeightChange} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">в неделю</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {(results.weeklyWeightChange * 4).toFixed(1)} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">в месяц</div>
            </div>
            
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm text-center">
              <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {results.goalWeight} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">целевой вес</div>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Примерное время до достижения цели:
            </span>
            <span className="flex items-center font-medium">
              <Clock className="w-4 h-4 mr-1 text-blue-500" />
              <span className="text-blue-700 dark:text-blue-300">
                {results.timeToGoal} {formatWeeks(results.timeToGoal)}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* Кнопка показа/скрытия подробностей */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full flex items-center justify-center py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors mb-4"
      >
        {showDetails ? (
          <>
            Скрыть подробности <ChevronUp className="w-4 h-4 ml-1" />
          </>
        ) : (
          <>
            Показать подробности <ChevronDown className="w-4 h-4 ml-1" />
          </>
        )}
      </button>

      {/* Детализированные результаты */}
      {showDetails && (
        <div className="space-y-3">
          <ResultItem 
            label="Базовый метаболизм (BMR)" 
            value={`${formatNumber(results.bmr, 0)} ккал/день`} 
            hint="Количество калорий, необходимых для поддержания основных функций организма в состоянии покоя"
            color="indigo" 
            icon={<Activity className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Суточная потребность с учетом активности (TDEE)" 
            value={`${formatNumber(results.tdee, 0)} ккал/день`} 
            hint={`Коэффициент активности: ${results.activityFactorUsed}`}
            color="purple" 
            icon={<Activity className="w-4 h-4"/>}
          />
          
          <ResultItem 
            label="Дефицит/профицит калорий" 
            value={`${results.calorieDeficitSurplus > 0 ? '+' : ''}${formatNumber(results.calorieDeficitSurplus, 0)} ккал/день`} 
            color={results.calorieDeficitSurplus < 0 ? "emerald" : results.calorieDeficitSurplus > 0 ? "blue" : "gray"}
          />
          
          <ResultItem 
            label="Еженедельное изменение веса" 
            value={`${results.weeklyWeightChange > 0 ? '+' : ''}${formatNumber(results.weeklyWeightChange, 2)} кг/неделю`} 
            color={results.weeklyWeightChange < 0 ? "emerald" : results.weeklyWeightChange > 0 ? "blue" : "gray"}
            icon={<Scale className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Целевой вес" 
            value={`${formatNumber(results.goalWeight, 1)} кг`} 
            color="indigo"
          />
          
          <ResultItem 
            label="Примерное время до цели" 
            value={`${formatNumber(results.timeToGoal, 0)} ${formatWeeks(results.timeToGoal)}`} 
            color="indigo" 
            icon={<Calendar className="w-4 h-4"/>} 
          />
          
          <ResultItem 
            label="Рекомендуемый тип питания" 
            value={results.dietType} 
          />
        </div>
      )}

      {/* Информационный блок о здоровом питании */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-1">
            <Apple className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важная информация</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Рекомендации основаны на формуле Миффлина-Сан Жеора и научных данных о здоровом питании.
              Помните, что это расчетные значения, которые могут отличаться от ваших индивидуальных потребностей.
              Для достижения наилучших результатов, регулярно следите за своим самочувствием и при необходимости
              корректируйте питание. Рекомендуем проконсультироваться с врачом или диетологом.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "неделя" в зависимости от числа
const formatWeeks = (weeks) => {
  const lastDigit = weeks % 10;
  const lastTwoDigits = weeks % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'недель';
  if (lastDigit === 1) return 'неделя';
  if (lastDigit >= 2 && lastDigit <= 4) return 'недели';
  return 'недель';
};

export default ResultsOverview;